import React, { useEffect, useState } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import StarIcon from "@mui/icons-material/Star";
import ReviewsOutlinedIcon from "@mui/icons-material/ReviewsOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CloseIcon from "@mui/icons-material/Close";

const API = process.env.REACT_APP_API_URL;

const AdminReviews = () => {
  const theme = useTheme();
  const isSmallMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [reviews, setReviews] = useState([]);
  const [selectedReview, setSelectedReview] = useState(null);
  const [openView, setOpenView] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const fetchReviews = async () => {
    setIsLoading(true);
    const loadingToast = toast.loading("Loading reviews...");
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API}reviews/`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setReviews(res.data);
      toast.dismiss(loadingToast);
      setIsLoading(false);
    } catch (err) {
      toast.dismiss(loadingToast);
      toast.error("Failed to fetch reviews");
      setIsLoading(false);
    }
  };

  const handleAction = async (type) => {
    const loadingToast = toast.loading(
      `${type === "approve" ? "Approving" : "Rejecting"} review...`
    );
    try {
      const token = localStorage.getItem("token");
      const url = `${API}reviews/${selectedReview.id}/${type}/`;
      await axios.post(
        url,
        {
          title: selectedReview.title,
          description: selectedReview.description,
          rating: selectedReview.rating,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      toast.dismiss(loadingToast);
      toast.success(`Review ${type}d successfully`);
      setOpenView(false);
      setSelectedReview(null);
      fetchReviews();
    } catch (err) {
      toast.dismiss(loadingToast);
      toast.error(`Failed to ${type} review`);
    }
  };

  const handleDelete = async () => {
    const loadingToast = toast.loading("Deleting review...");
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API}reviews/${selectedReview.id}/`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.dismiss(loadingToast);
      toast.success("Review deleted successfully");
      setOpenDelete(false);
      setSelectedReview(null);
      fetchReviews();
    } catch (err) {
      toast.dismiss(loadingToast);
      toast.error("Failed to delete review");
    }
  };

  const getStatusColor = (isApproved) => {
    return isApproved
      ? "bg-green-100 text-green-800 border-green-200"
      : "bg-yellow-100 text-yellow-800 border-yellow-200";
  };

  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    const options = {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    };
    return date.toLocaleString("en-IN", options);
  };

  const ReviewCard = ({ review }) => (
    <div className="bg-white rounded-lg shadow-md p-4 border border-gray-200 hover:shadow-lg transition-shadow duration-200">
      <div className="space-y-3">
        {/* Header with Status */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ReviewsOutlinedIcon className="text-yellow-600 text-lg" />
            <span className="font-semibold text-gray-900">{review.title}</span>
          </div>
          <span
            className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(
              review.is_approved
            )}`}
          >
            {review.is_approved ? "APPROVED" : "PENDING"}
          </span>
        </div>

        {/* Review Details */}
        <div className="space-y-2 p-3 bg-gray-50 rounded-lg">
          <div className="grid grid-cols-2 gap-2 text-sm">
            <div>
              <span className="text-gray-500">ID:</span>
              <span className="font-mono text-gray-900 ml-1">{review.id}</span>
            </div>
            <div>
              <span className="text-gray-500">Rating:</span>
              <span className="text-yellow-500 ml-1">
                {[...Array(review.rating)].map((_, i) => (
                  <StarIcon key={i} fontSize="small" />
                ))}
              </span>
            </div>
          </div>
          <div>
            <span className="text-gray-500">Author:</span>
            <span className="font-medium text-gray-900 ml-1">
              {review.name}
            </span>
          </div>
          <div>
            <span className="text-gray-500">Description:</span>
            <span className="text-gray-900 ml-1 line-clamp-2">
              {review.description}
            </span>
          </div>
          <div>
            <span className="text-gray-500">Submitted:</span>
            <span className="text-gray-900 ml-1 text-sm">
              {formatDateTime(review.submitted_on)}
            </span>
          </div>
        </div>

        {/* Actions */}
        <div className="flex flex-wrap gap-2 pt-2 border-t border-gray-200">
          <button
            onClick={() => {
              setSelectedReview(review);
              setOpenView(true);
            }}
            className="bg-blue-600 text-white px-3 py-2 rounded-lg text-xs hover:bg-blue-700 transition-colors duration-200 shadow-sm flex-1 flex items-center justify-center gap-1"
          >
            <VisibilityIcon className="text-sm" />
            View Details
          </button>
          {!review.is_approved && (
            <button
              onClick={() => {
                setSelectedReview(review);
                handleAction("approve");
              }}
              className="bg-green-600 text-white px-3 py-2 rounded-lg text-xs hover:bg-green-700 transition-colors duration-200 shadow-sm flex-1 flex items-center justify-center gap-1"
            >
              <CheckCircleIcon className="text-sm" />
              Approve
            </button>
          )}
          <button
            onClick={() => {
              setSelectedReview(review);
              setOpenDelete(true);
            }}
            className="bg-red-600 text-white px-3 py-2 rounded-lg text-xs hover:bg-red-700 transition-colors duration-200 shadow-sm flex-1 flex items-center justify-center gap-1"
          >
            <DeleteIcon className="text-sm" />
            Delete
          </button>
        </div>
      </div>
    </div>
  );

  useEffect(() => {
    fetchReviews();
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-4 md:p-6">
      <div className="max-w-full mx-auto">
        {/* Refresh Button */}
        <div className="flex justify-end mb-4">
          <button
            onClick={fetchReviews}
            disabled={isLoading}
            className="flex items-center gap-2 bg-white text-gray-600 p-2 rounded-lg hover:bg-gray-50 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
            title="Refresh reviews"
          >
            <RefreshIcon
              className={`text-sm ${isLoading ? "animate-spin" : ""}`}
            />
          </button>
        </div>

        {/* Mobile Cards View */}
        <div className="block md:hidden">
          {reviews.length === 0 ? (
            <div className="bg-white rounded-xl shadow-lg p-8 text-center">
              <div className="flex flex-col items-center gap-2">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center">
                  <ReviewsOutlinedIcon className="text-gray-400 text-2xl" />
                </div>
                <p className="text-gray-500 font-medium">No reviews found</p>
                <p className="text-gray-400 text-sm">
                  No student reviews have been submitted yet
                </p>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {reviews.map((review, index) => (
                <ReviewCard key={index} review={review} />
              ))}
            </div>
          )}
        </div>

        {/* Desktop Table View */}
        <div className="hidden md:block bg-white rounded-xl shadow-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead className="bg-gradient-to-r from-yellow-600 to-orange-700 text-white">
                <tr>
                  <th className="px-4 py-4 text-left text-sm font-semibold">
                    ID
                  </th>
                  <th className="px-4 py-4 text-left text-sm font-semibold">
                    Author
                  </th>
                  <th className="px-4 py-4 text-left text-sm font-semibold">
                    Title
                  </th>
                  <th className="px-4 py-4 text-left text-sm font-semibold">
                    Rating
                  </th>
                  <th className="px-4 py-4 text-left text-sm font-semibold">
                    Status
                  </th>
                  <th className="px-4 py-4 text-left text-sm font-semibold">
                    Submitted On
                  </th>
                  <th className="px-4 py-4 text-left text-sm font-semibold">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {reviews.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="px-4 py-8 text-center">
                      <div className="flex flex-col items-center gap-2">
                        <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center">
                          <ReviewsOutlinedIcon className="text-gray-400 text-2xl" />
                        </div>
                        <p className="text-gray-500 font-medium">
                          No reviews found
                        </p>
                        <p className="text-gray-400 text-sm">
                          No student reviews have been submitted yet
                        </p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  reviews.map((review) => (
                    <tr
                      key={review.id}
                      className="hover:bg-gray-50 transition-colors duration-200"
                    >
                      <td className="px-4 py-4 font-mono text-gray-600">
                        {review.id}
                      </td>
                      <td className="px-4 py-4">
                        <div className="font-medium text-gray-900">
                          {review.name}
                        </div>
                      </td>
                      <td className="px-4 py-4 text-gray-900 font-medium">
                        {review.title}
                      </td>
                      <td className="px-4 py-4 text-yellow-500">
                        {[...Array(review.rating)].map((_, i) => (
                          <StarIcon key={i} fontSize="small" />
                        ))}
                      </td>
                      <td className="px-4 py-4">
                        <span
                          className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(
                            review.is_approved
                          )}`}
                        >
                          {review.is_approved ? "APPROVED" : "PENDING"}
                        </span>
                      </td>
                      <td className="px-4 py-4 text-gray-600 text-sm">
                        {formatDateTime(review.submitted_on)}
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex flex-col gap-2">
                          <button
                            onClick={() => {
                              setSelectedReview(review);
                              setOpenView(true);
                            }}
                            className="bg-blue-600 text-white px-3 py-2 rounded-lg text-xs hover:bg-blue-700 transition-colors duration-200 shadow-sm flex items-center gap-1"
                          >
                            <VisibilityIcon className="text-sm" />
                            View
                          </button>
                          {!review.is_approved && (
                            <button
                              onClick={() => {
                                setSelectedReview(review);
                                handleAction("approve");
                              }}
                              className="bg-green-600 text-white px-3 py-2 rounded-lg text-xs hover:bg-green-700 transition-colors duration-200 shadow-sm flex items-center gap-1"
                            >
                              <CheckCircleIcon className="text-sm" />
                              Approve
                            </button>
                          )}
                          <button
                            onClick={() => {
                              setSelectedReview(review);
                              setOpenDelete(true);
                            }}
                            className="bg-red-600 text-white px-3 py-2 rounded-lg text-xs hover:bg-red-700 transition-colors duration-200 shadow-sm flex items-center gap-1"
                          >
                            <DeleteIcon className="text-sm" />
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* View Modal */}
      <Dialog
        open={openView}
        onClose={() => setOpenView(false)}
        fullWidth
        maxWidth={isSmallMobile ? "xs" : "sm"}
        PaperProps={{
          sx: {
            borderRadius: "16px",
            margin: isSmallMobile ? "16px" : "32px",
          },
        }}
      >
        <DialogTitle className="bg-gradient-to-r from-yellow-600 to-orange-600 text-white p-6">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-white/20 rounded-lg">
              <ReviewsOutlinedIcon className="text-white" />
            </div>
            <Typography variant="h6" className="font-bold">
              Review Details
            </Typography>
          </div>
        </DialogTitle>
        <DialogContent className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4 text-sm mt-3">
            <div>
              <span className="text-gray-500 font-medium">ID:</span>
              <span className="font-mono text-gray-900 ml-2">
                {selectedReview?.id}
              </span>
            </div>
            <div>
              <span className="text-gray-500 font-medium">Status:</span>
              <span
                className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border ml-2 ${getStatusColor(
                  selectedReview?.is_approved
                )}`}
              >
                {selectedReview?.is_approved ? "APPROVED" : "PENDING"}
              </span>
            </div>
          </div>

          <div>
            <span className="text-gray-500 font-medium">Author:</span>
            <span className="text-gray-900 ml-2 font-medium">
              {selectedReview?.name}
            </span>
          </div>

          <div>
            <span className="text-gray-500 font-medium">Title:</span>
            <span className="text-gray-900 ml-2 font-medium">
              {selectedReview?.title}
            </span>
          </div>

          <div>
            <span className="text-gray-500 font-medium">Rating:</span>
            <span className="text-yellow-500 ml-2">
              {[...Array(selectedReview?.rating || 0)].map((_, i) => (
                <StarIcon key={i} fontSize="small" />
              ))}
            </span>
          </div>

          <div>
            <span className="text-gray-500 font-medium">Description:</span>
            <div className="mt-2 p-3 bg-gray-50 rounded-lg">
              <span className="text-gray-900">
                {selectedReview?.description}
              </span>
            </div>
          </div>

          <div>
            <span className="text-gray-500 font-medium">Submitted On:</span>
            <span className="text-gray-900 ml-2">
              {selectedReview?.submitted_on
                ? formatDateTime(selectedReview.submitted_on)
                : "—"}
            </span>
          </div>
        </DialogContent>
        <DialogActions className="p-6 pt-0 gap-3">
          {!selectedReview?.is_approved && (
            <Button
              variant="contained"
              color="success"
              onClick={() => handleAction("approve")}
              className="bg-green-600 hover:bg-green-700"
              sx={{ borderRadius: "12px" }}
              startIcon={<CheckCircleIcon />}
            >
              Approve Review
            </Button>
          )}
          <Button
            onClick={() => setOpenView(false)}
            variant="outlined"
            sx={{
              borderRadius: "12px",
              borderColor: "#d1d5db",
              color: "#374151",
              "&:hover": {
                borderColor: "#9ca3af",
                backgroundColor: "#f9fafb",
              },
            }}
            startIcon={<CloseIcon />}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog
        open={openDelete}
        onClose={() => setOpenDelete(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "16px",
            margin: isSmallMobile ? "16px" : "32px",
          },
        }}
      >
        <DialogTitle className="flex items-center gap-3 text-red-600 p-6">
          <div className="p-2 bg-red-100 rounded-lg">
            <DeleteIcon color="error" />
          </div>
          <Typography variant="h6" className="font-bold">
            Confirm Delete
          </Typography>
        </DialogTitle>
        <DialogContent className="p-6 pt-0">
          <Typography variant="body1" className="text-gray-700 leading-relaxed">
            Are you sure you want to delete this review from{" "}
            {selectedReview?.name}? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions className="p-6 pt-0 gap-3">
          <Button
            onClick={() => setOpenDelete(false)}
            variant="outlined"
            fullWidth={isSmallMobile}
            sx={{
              borderRadius: "12px",
              borderColor: "#d1d5db",
              color: "#374151",
              "&:hover": {
                borderColor: "#9ca3af",
                backgroundColor: "#f9fafb",
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDelete}
            variant="contained"
            color="error"
            fullWidth={isSmallMobile}
            className="bg-red-600 hover:bg-red-700"
            sx={{ borderRadius: "12px" }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default AdminReviews;
